import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "medicationsList",
    "medicationsFields",
    "medicationTemplate",
    "medicationRelatedCheckbox",
    "step",
    "stepIndicator",
    "prevButton",
    "nextButton",
    "submitButton",
    "nidrButton",
    "currentStepNumber",
    "currentStepTitle",
    "progressDot",
    "mobileStepMenu",
    "mobileStepIndicator",
    "mobileMenuChevron"
  ]

  static values = {
    currentStep: { type: Number, default: 0 },
    totalSteps: Number,
    hasErrors: { type: Boolean, default: false },
    autosave: { type: Boolean, default: false },
    autosaveUrl: String
  }

  connect() {
    this.medicationIndex = this.medicationsListTarget?.querySelectorAll('.medication-entry').length || 0
    this.totalStepsValue = this.stepTargets.length

    this.syncMedicationEntries()

    // Step titles for mobile indicator
    this.stepTitles = [
      "Date & Time",
      "Incident Details",
      "Medications",
      "Patient Info",
      "Outcome",
      "Description",
      "Contributing Factors",
      "Review"
    ]

    // Track which steps have been completed
    this.completedSteps = new Set()

    // Validate all steps on initial render to determine completion state
    this.validateAllSteps()

    // If there are validation errors, go to the Review step to show them
    // Otherwise, find the first incomplete step
    if (this.hasErrorsValue) {
      this.currentStepValue = this.totalStepsValue - 1
    } else {
      this.currentStepValue = this.findFirstIncompleteStep()
    }

    this.showStep(this.currentStepValue)

    // Setup autosave if enabled
    if (this.autosaveValue) {
      this.setupAutosave()
    }
  }

  // Find the first step that is not complete (not in completedSteps)
  findFirstIncompleteStep() {
    for (let i = 0; i < this.totalStepsValue - 1; i++) {
      if (!this.completedSteps.has(i)) {
        return i
      }
    }
    // All steps complete, go to Review
    return this.totalStepsValue - 1
  }

  disconnect() {
    if (this.autosaveTimeout) {
      clearTimeout(this.autosaveTimeout)
    }
  }

  setupAutosave() {
    // Debounce time in milliseconds
    this.autosaveDelay = 2000
    this.autosaveTimeout = null
    this.lastSavedData = null

    // Listen to form changes
    this.element.addEventListener("input", this.scheduleAutosave.bind(this))
    this.element.addEventListener("change", this.scheduleAutosave.bind(this))
  }

  scheduleAutosave() {
    if (!this.autosaveValue) return

    if (this.autosaveTimeout) {
      clearTimeout(this.autosaveTimeout)
    }

    this.autosaveTimeout = setTimeout(() => {
      this.performAutosave()
    }, this.autosaveDelay)
  }

  async performAutosave() {
    if (!this.autosaveValue || !this.autosaveUrlValue) return

    const formData = new FormData(this.element)

    // Show saving indicator
    this.updateAutosaveIndicator("saving")

    try {
      const response = await fetch(this.autosaveUrlValue, {
        method: "PATCH",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Accept": "application/json"
        }
      })

      if (response.ok) {
        const data = await response.json()
        this.updateAutosaveIndicator("saved", data.updated_at)
        this.syncMedicationIds(data.medications)
      } else {
        this.updateAutosaveIndicator("error")
      }
    } catch (error) {
      console.error("Autosave error:", error)
      this.updateAutosaveIndicator("error")
    }
  }

  updateAutosaveIndicator(status, timestamp = null) {
    // Dispatch event for header controller to handle
    this.dispatch("autosave", { detail: { status, timestamp } })
  }

  validateAllSteps() {
    // Review has no required fields; keep it out of completedSteps so it is not a free jump target.
    for (let i = 0; i < this.reviewStepIndex(); i++) {
      if (this.isStepValid(i)) {
        this.completedSteps.add(i)
      }
    }
  }

  reviewStepIndex() {
    return this.totalStepsValue - 1
  }

  // Step Navigation
  nextStep(event) {
    event.preventDefault()
    if (this.validateStepWithFeedback(this.currentStepValue) && this.currentStepValue < this.reviewStepIndex()) {
      this.currentStepValue++
      this.showStep(this.currentStepValue)
    }
  }

  prevStep(event) {
    event.preventDefault()
    if (this.currentStepValue > 0) {
      this.validateAndUpdateStep(this.currentStepValue)
      this.currentStepValue--
      this.showStep(this.currentStepValue)
    }
  }

  goToStep(event) {
    event.preventDefault()
    this.attemptGoToStep(parseInt(event.currentTarget.dataset.stepIndex, 10))
  }

  attemptGoToStep(stepIndex) {
    if (Number.isNaN(stepIndex) || stepIndex < 0 || stepIndex >= this.totalStepsValue) return
    if (stepIndex === this.currentStepValue) return

    if (stepIndex > this.currentStepValue) {
      if (!this.validateStepWithFeedback(this.currentStepValue)) return

      const invalid = this.firstInvalidStep(this.currentStepValue + 1, stepIndex)
      if (invalid !== null) {
        this.validateAndUpdateStep(this.currentStepValue)
        this.validateStepWithFeedback(invalid)
        this.currentStepValue = invalid
        this.showStep(invalid)
        return
      }
    }

    this.validateAndUpdateStep(this.currentStepValue)
    this.currentStepValue = stepIndex
    this.showStep(this.currentStepValue)
  }

  handleSubmit(event) {
    const invalid = this.firstInvalidStep(0, this.reviewStepIndex())
    if (invalid === null) return

    event.preventDefault()
    this.validateStepWithFeedback(invalid)
    this.currentStepValue = invalid
    this.showStep(invalid)
  }

  firstInvalidStep(from, to) {
    for (let i = from; i < to; i++) {
      if (this.isStepValid(i)) {
        this.completedSteps.add(i)
        continue
      }

      return i
    }

    return null
  }

  showStep(index) {
    this.stepTargets.forEach((step, i) => {
      step.classList.toggle("hidden", i !== index)
    })

    this.updateStepIndicators(index)

    // Update navigation buttons
    this.prevButtonTarget.classList.toggle("hidden", index === 0)
    this.nextButtonTarget.classList.toggle("hidden", index === this.totalStepsValue - 1)
    this.submitButtonTarget.classList.toggle("hidden", index !== this.totalStepsValue - 1)
    if (this.hasNidrButtonTarget) {
      this.nidrButtonTarget.classList.toggle("hidden", index !== this.totalStepsValue - 1)
    }

    // Populate review summary on last step
    if (index === this.totalStepsValue - 1) {
      this.populateReviewSummary()
    }

    // Scroll to top of form
    this.element.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  updateStepIndicators(index) {
    this.stepIndicatorTargets.forEach((indicator, i) => {
      const isCompleted = this.completedSteps.has(i)
      const isCurrent = i === index

      indicator.classList.remove("step-completed", "step-current", "step-upcoming")

      if (isCurrent) {
        indicator.classList.add("step-current")
      } else if (isCompleted) {
        indicator.classList.add("step-completed")
      } else {
        indicator.classList.add("step-upcoming")
      }
    })

    if (this.hasCurrentStepNumberTarget) {
      this.currentStepNumberTarget.textContent = index + 1
    }
    if (this.hasCurrentStepTitleTarget) {
      this.currentStepTitleTarget.textContent = this.stepTitles[index] || ""
    }

    this.progressDotTargets.forEach((dot, i) => {
      const isCompleted = this.completedSteps.has(i)
      const isCurrent = i === index

      dot.classList.remove("bg-slate-200", "bg-primary-600", "bg-green-500")

      if (isCurrent) {
        dot.classList.add("bg-primary-600")
      } else if (isCompleted) {
        dot.classList.add("bg-green-500")
      } else {
        dot.classList.add("bg-slate-200")
      }
    })

    this.mobileStepIndicatorTargets.forEach((indicator, i) => {
      const isCompleted = this.completedSteps.has(i)
      const isCurrent = i === index

      indicator.classList.remove("step-completed", "step-current", "step-upcoming")

      if (isCurrent) {
        indicator.classList.add("step-current")
      } else if (isCompleted) {
        indicator.classList.add("step-completed")
      } else {
        indicator.classList.add("step-upcoming")
      }
    })
  }

  populateReviewSummary() {
    const form = this.element

    // Date
    const dateField = form.querySelector('[name="incident[occurred_on]"]')
    const reviewDate = document.getElementById("review-date")
    if (dateField && reviewDate) {
      reviewDate.textContent = dateField.value ? this.formatDate(dateField.value) : "-"
    }

    // Time Range
    const timeRangeField = form.querySelector('[name="incident[time_range]"]')
    const reviewTimeRange = document.getElementById("review-time-range")
    if (timeRangeField && reviewTimeRange) {
      const selectedOption = timeRangeField.options[timeRangeField.selectedIndex]
      reviewTimeRange.textContent = selectedOption && selectedOption.value ? selectedOption.text : "-"
    }

    // Incident Type
    const typeField = form.querySelector('[name="incident[incident_type]"]')
    const reviewType = document.getElementById("review-type")
    if (typeField && reviewType) {
      const selectedOption = typeField.options[typeField.selectedIndex]
      reviewType.textContent = selectedOption && selectedOption.value ? selectedOption.text : "-"
    }

    // Discovered By
    const discoveredByField = form.querySelector('[name="incident[discovered_by]"]')
    const reviewDiscoveredBy = document.getElementById("review-discovered-by")
    if (discoveredByField && reviewDiscoveredBy) {
      const selectedOption = discoveredByField.options[discoveredByField.selectedIndex]
      reviewDiscoveredBy.textContent = selectedOption && selectedOption.value ? selectedOption.text : "-"
    }

    // Medication Stages (checkboxes)
    const stagesChecked = form.querySelectorAll('[name="incident[medication_stages][]"]:checked')
    const reviewStages = document.getElementById("review-medication-stages")
    if (reviewStages) {
      if (stagesChecked.length > 0) {
        const stageLabels = Array.from(stagesChecked).map(cb => {
          const label = cb.closest('label')
          return label ? label.textContent.trim() : cb.value
        })
        reviewStages.textContent = stageLabels.join(", ")
      } else {
        reviewStages.textContent = "-"
      }
    }

    // Medication Related
    const medicationRelated = this.isMedicationRelated()
    const reviewMed = document.getElementById("review-medication")
    if (reviewMed) {
      reviewMed.textContent = medicationRelated ? "Yes" : "No"
    }

    // Medications List
    const reviewMedicationsList = document.getElementById("review-medications-list")
    const reviewMedicationsItems = document.getElementById("review-medications-items")
    if (reviewMedicationsList && reviewMedicationsItems) {
      reviewMedicationsItems.innerHTML = ""
      if (medicationRelated && this.getVisibleMedicationCount() > 0) {
        reviewMedicationsList.classList.remove("hidden")
        this.visibleMedicationEntries().forEach(med => {
          const drugName = med.querySelector('[name*="[drug_name]"]')?.value || "-"
          const din = med.querySelector('[name*="[din]"]')?.value
          const medItem = document.createElement("div")
          medItem.className = "text-slate-900"
          medItem.textContent = din ? `${drugName} (${din})` : drugName
          reviewMedicationsItems.appendChild(medItem)
        })
      } else {
        reviewMedicationsList.classList.add("hidden")
      }
    }

    // Prescriber Type
    const prescriberField = form.querySelector('[name="incident[prescriber_type]"]')
    const reviewPrescriber = document.getElementById("review-prescriber-type")
    if (prescriberField && reviewPrescriber) {
      const selectedOption = prescriberField.options[prescriberField.selectedIndex]
      reviewPrescriber.textContent = selectedOption && selectedOption.value ? selectedOption.text : "-"
    }

    // Patient Gender
    const genderField = form.querySelector('[name="incident[patient_gender]"]')
    const reviewGender = document.getElementById("review-patient-gender")
    if (genderField && reviewGender) {
      const selectedOption = genderField.options[genderField.selectedIndex]
      reviewGender.textContent = selectedOption && selectedOption.value ? selectedOption.text : "-"
    }

    // Patient Age Range
    const ageField = form.querySelector('[name="incident[patient_age_range]"]')
    const reviewAge = document.getElementById("review-patient-age")
    if (ageField && reviewAge) {
      const selectedOption = ageField.options[ageField.selectedIndex]
      reviewAge.textContent = selectedOption && selectedOption.value ? selectedOption.text : "-"
    }

    // Degree of Harm
    const harmField = form.querySelector('[name="incident[degree_of_harm]"]')
    const reviewHarm = document.getElementById("review-harm")
    if (harmField && reviewHarm) {
      const selectedOption = harmField.options[harmField.selectedIndex]
      reviewHarm.textContent = selectedOption && selectedOption.value ? selectedOption.text : "-"
    }

    // Description
    const descriptionField = form.querySelector('[name="incident[description]"]')
    const reviewDescription = document.getElementById("review-description")
    if (descriptionField && reviewDescription) {
      reviewDescription.textContent = descriptionField.value || "-"
    }

    // Actions Taken
    const actionsField = form.querySelector('[name="incident[actions_taken]"]')
    const reviewActions = document.getElementById("review-actions-taken")
    if (actionsField && reviewActions) {
      reviewActions.textContent = actionsField.value || "-"
    }

    // Shared Learning (optional - hide if empty)
    const sharedLearningField = form.querySelector('[name="incident[shared_learning]"]')
    const reviewSharedLearning = document.getElementById("review-shared-learning")
    const reviewSharedLearningContainer = document.getElementById("review-shared-learning-container")
    if (sharedLearningField && reviewSharedLearning && reviewSharedLearningContainer) {
      if (sharedLearningField.value) {
        reviewSharedLearning.textContent = sharedLearningField.value
        reviewSharedLearningContainer.classList.remove("hidden")
      } else {
        reviewSharedLearningContainer.classList.add("hidden")
      }
    }

    // Background Info (optional - hide if empty)
    const backgroundChecked = form.querySelectorAll('[name="incident[background_info][]"]:checked')
    const reviewBackgroundInfo = document.getElementById("review-background-info")
    const reviewBackgroundInfoContainer = document.getElementById("review-background-info-container")
    if (reviewBackgroundInfo && reviewBackgroundInfoContainer) {
      if (backgroundChecked.length > 0) {
        const labels = Array.from(backgroundChecked).map(cb => {
          const label = cb.closest('label')
          return label ? label.textContent.trim() : cb.value
        })
        reviewBackgroundInfo.textContent = labels.join(", ")
        reviewBackgroundInfoContainer.classList.remove("hidden")
      } else {
        reviewBackgroundInfoContainer.classList.add("hidden")
      }
    }

    // Contributing Factors
    const factorsChecked = form.querySelectorAll('[name="incident[contributing_factors][]"]:checked')
    const reviewFactors = document.getElementById("review-contributing-factors")
    if (reviewFactors) {
      if (factorsChecked.length > 0) {
        const labels = Array.from(factorsChecked).map(cb => {
          const label = cb.closest('label')
          return label ? label.textContent.trim() : cb.value
        })
        reviewFactors.textContent = labels.join(", ")
      } else {
        reviewFactors.textContent = "-"
      }
    }
  }

  formatDate(dateString) {
    const date = new Date(dateString + "T00:00:00")
    return date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
  }

  isStepValid(stepIndex) {
    return this.validateStep(stepIndex, false)
  }

  isMedicationRelated() {
    return this.hasMedicationRelatedCheckboxTarget && this.medicationRelatedCheckboxTarget.checked
  }

  visibleMedicationEntries() {
    if (!this.hasMedicationsListTarget) return []
    return Array.from(this.medicationsListTarget.querySelectorAll('.medication-entry:not(.hidden)'))
  }

  getVisibleMedicationCount() {
    return this.visibleMedicationEntries().length
  }

  validateAndUpdateStep(stepIndex) {
    const isValid = this.isStepValid(stepIndex)
    if (isValid) {
      this.completedSteps.add(stepIndex)
    } else {
      this.completedSteps.delete(stepIndex)
    }
    return isValid
  }

  validateStepWithFeedback(stepIndex) {
    const isValid = this.validateStep(stepIndex, true)
    if (isValid) {
      this.completedSteps.add(stepIndex)
    } else {
      this.completedSteps.delete(stepIndex)
    }
    return isValid
  }

  validateStep(stepIndex, feedback) {
    const stepEl = this.stepTargets[stepIndex]
    let isValid = true

    stepEl.querySelectorAll("[required]").forEach(field => {
      const empty = !field.value?.trim()
      if (feedback) field.classList.toggle("border-red-500", empty)
      if (empty) isValid = false
    })

    stepEl.querySelectorAll("[data-checkbox-group-required]").forEach(group => {
      const empty = group.querySelectorAll("input[type=checkbox]:checked").length === 0
      if (feedback) {
        group.classList.toggle("border", empty)
        group.classList.toggle("border-red-500", empty)
        group.classList.toggle("rounded", empty)
        group.classList.toggle("p-2", empty)
      }
      if (empty) isValid = false
    })

    return isValid
  }

  toggleMedicationRelated() {
    this.syncMedicationEntries({ refreshStep: true })
    this.scheduleAutosave()
  }

  syncMedicationEntries({ refreshStep = false } = {}) {
    const related = this.isMedicationRelated()

    if (this.hasMedicationsFieldsTarget) {
      this.medicationsFieldsTarget.classList.toggle("hidden", !related)
    }

    if (related) {
      if (this.getVisibleMedicationCount() === 0) this.insertMedicationEntry()
    } else {
      this.visibleMedicationEntries().forEach(entry => this.destroyMedicationEntry(entry))
    }

    this.syncMedicationRemoveButtons()
    if (refreshStep) this.refreshMedicationsStepCompletion()
  }

  syncMedicationRemoveButtons() {
    const entries = this.visibleMedicationEntries()
    const hideRemove = this.isMedicationRelated() && entries.length <= 1
    entries.forEach(entry => {
      const button = entry.querySelector('[data-action*="removeMedication"]')
      if (button) button.classList.toggle("hidden", hideRemove)
    })
  }

  refreshMedicationsStepCompletion() {
    const stepIndex = this.medicationsStepIndex()
    if (stepIndex < 0) return

    this.validateAndUpdateStep(stepIndex)
    if (!this.completedSteps.has(stepIndex)) {
      this.invalidateLaterSteps(stepIndex)
    }
    this.updateStepIndicators(this.currentStepValue)
  }

  medicationsStepIndex() {
    if (!this.hasMedicationsListTarget) return -1
    return this.stepTargets.findIndex(step => step.contains(this.medicationsListTarget))
  }

  invalidateLaterSteps(fromIndex) {
    for (let i = fromIndex + 1; i < this.totalStepsValue; i++) {
      this.completedSteps.delete(i)
    }
  }

  addMedication(event) {
    event.preventDefault()
    if (!this.isMedicationRelated()) return
    this.insertMedicationEntry()
    this.syncMedicationRemoveButtons()
    this.refreshMedicationsStepCompletion()
  }

  insertMedicationEntry() {
    if (this.hasMedicationTemplateTarget && this.hasMedicationsListTarget) {
      const template = this.medicationTemplateTarget.innerHTML
      const newEntry = template.replace(/NEW_INDEX/g, this.medicationIndex)

      this.medicationsListTarget.insertAdjacentHTML("beforeend", newEntry)
      this.medicationIndex++
    }
  }

  removeMedication(event) {
    event.preventDefault()
    if (this.isMedicationRelated() && this.getVisibleMedicationCount() <= 1) return

    const entry = event.target.closest(".medication-entry")
    if (entry) this.destroyMedicationEntry(entry)
    this.syncMedicationRemoveButtons()
    this.refreshMedicationsStepCompletion()
  }

  destroyMedicationEntry(entry) {
    const destroyField = entry.querySelector('[data-incident-form-target="destroyField"]')

    entry.querySelectorAll('input[name*="[drug_name]"], input[name*="[din]"]').forEach(field => {
      field.required = false
    })

    if (destroyField) {
      destroyField.value = "1"
      entry.classList.add("hidden")
    } else {
      entry.remove()
    }
  }

  // Sync medication IDs from autosave response so subsequent saves update instead of duplicating
  syncMedicationIds(medications) {
    if (!medications || !this.hasMedicationsListTarget) return

    // Remove destroyed entries from DOM — they've been deleted server-side
    this.medicationsListTarget.querySelectorAll('.medication-entry.hidden').forEach(entry => entry.remove())

    // Sync IDs for remaining entries using their actual form field index
    const entries = this.medicationsListTarget.querySelectorAll('.medication-entry')
    entries.forEach((entry, index) => {
      if (index >= medications.length) return

      const med = medications[index]

      // Find the actual name index used by this entry's fields
      const existingField = entry.querySelector('input[name*="incident_medications_attributes"]')
      if (!existingField) return

      const match = existingField.name.match(/incident_medications_attributes\]\[(\d+)\]/)
      if (!match) return

      const namePrefix = `incident[incident_medications_attributes][${match[1]}]`
      let idField = entry.querySelector(`input[name="${namePrefix}[id]"]`)

      if (!idField) {
        idField = document.createElement("input")
        idField.type = "hidden"
        idField.name = `${namePrefix}[id]`
        entry.appendChild(idField)
      }

      idField.value = med.id
    })
  }

  // Mobile step menu
  toggleMobileStepMenu(event) {
    event.preventDefault()
    if (this.hasMobileStepMenuTarget) {
      const isHidden = this.mobileStepMenuTarget.classList.contains("hidden")
      this.mobileStepMenuTarget.classList.toggle("hidden")

      // Rotate chevron
      if (this.hasMobileMenuChevronTarget) {
        this.mobileMenuChevronTarget.classList.toggle("rotate-180", isHidden)
      }

      // Close menu when clicking outside
      if (isHidden) {
        this.boundCloseMobileMenu = this.closeMobileMenuOnClickOutside.bind(this)
        document.addEventListener("click", this.boundCloseMobileMenu)
      }
    }
  }

  closeMobileMenuOnClickOutside(event) {
    if (this.hasMobileStepMenuTarget && !this.element.contains(event.target)) {
      this.closeMobileMenu()
    }
  }

  closeMobileMenu() {
    if (this.hasMobileStepMenuTarget) {
      this.mobileStepMenuTarget.classList.add("hidden")
      if (this.hasMobileMenuChevronTarget) {
        this.mobileMenuChevronTarget.classList.remove("rotate-180")
      }
      if (this.boundCloseMobileMenu) {
        document.removeEventListener("click", this.boundCloseMobileMenu)
      }
    }
  }

  goToStepMobile(event) {
    event.preventDefault()
    event.stopPropagation()
    this.attemptGoToStep(parseInt(event.currentTarget.dataset.stepIndex, 10))
    this.closeMobileMenu()
  }
}
